import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { TimezoneCombobox } from '@/components/ui/timezone-combobox';
import { api, apiMessage } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import type { Me } from '@/lib/types';

async function fetchMe(): Promise<Me> {
  const { data } = await api.get<Me>('/v1/me');
  return data;
}

export default function Profile() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me, queryFn: fetchMe });
  const [timezone, setTimezone] = useState<string>('UTC');

  // Sync local edit state when /me resolves or refetches.
  useEffect(() => {
    if (me.data?.timezone) setTimezone(me.data.timezone);
  }, [me.data?.timezone]);

  const save = useMutation({
    mutationFn: async (tz: string) => {
      const { data } = await api.patch<Me>('/v1/me', { timezone: tz });
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.me, data);
      // Bucketing reshuffles — invalidate every analytics surface.
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: `Timezone updated to ${data.timezone}` });
    },
    onError: (err) => {
      toast({
        title: apiMessage(err, 'Could not save timezone'),
        variant: 'destructive',
      });
    },
  });

  const dirty = !!me.data && timezone !== me.data.timezone;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Tenant-wide settings that affect how the dashboard buckets your data.
        </p>
      </header>

      {me.isLoading && <Skeleton className="h-40 w-full" />}
      {me.isError && (
        <p className="text-sm text-destructive">Could not load profile.</p>
      )}

      {me.data && (
        <Card>
          <CardHeader>
            <CardTitle>Analytics timezone</CardTitle>
            <CardDescription>
              All charts and counters bucket by day / hour in this zone. Events
              are still stored as absolute UTC instants — changing this only
              re-buckets existing data, it doesn't lose or rewrite anything.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 sm:max-w-md">
              <Label htmlFor="timezone">IANA timezone</Label>
              <TimezoneCombobox
                id="timezone"
                value={timezone}
                onChange={setTimezone}
              />
              <p className="text-xs text-muted-foreground">
                Type to filter by city, offset, or IANA id.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => save.mutate(timezone)}
                disabled={!dirty || save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save timezone'}
              </Button>
              {dirty && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setTimezone(me.data.timezone)}
                  disabled={save.isPending}
                >
                  Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
